//frontend/src/components/SearchManagerRequests/SearchManagerRequestsList.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getCurrentUser,
  getRequestSelections,
} from "../api/requestSelection/requestSelectionApi";

import { StatusBadge, STATUS_OPTIONS } from "./constants";
import "./SearchManagerRequests.css";

export default function SearchManagerRequestsList() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [me, setMe] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [user, items] = await Promise.all([
          getCurrentUser(),
          getRequestSelections(),
        ]);
        setMe(user);
        setRequests(Array.isArray(items) ? items : []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const myRequests = useMemo(() => {
    if (!me?.id) return [];
    return requests.filter((r) => r.search_manager_id === me.id);
  }, [requests, me]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return myRequests.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesSearch =
        !q ||
        String(r.number || "").toLowerCase().includes(q) ||
        String(r.title || "").toLowerCase().includes(q) ||
        String(r.description || "").toLowerCase().includes(q) ||
        String(r.client_name || "").toLowerCase().includes(q) ||
        String(r.client_manager_name || "").toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [myRequests, search, status]);

  return (
    <div className="proc-page">
      <div className="proc-page__header">
        <div>
          <div className="proc-breadcrumb">Procurement / Заявки</div>
          <h1>Мои заявки на поиск</h1>
          <p>Заявки, назначенные вам на обработку</p>
        </div>
      </div>

      <div className="proc-toolbar">
        <div className="proc-filters">
          <input
            className="proc-input"
            placeholder="Поиск по номеру, товару, клиенту, администратору..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="proc-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="proc-empty proc-empty--large">
          <h3>Загрузка заявок...</h3>
        </div>
      )}

      {!loading && error && (
        <div className="proc-empty proc-empty--large">
          <div className="proc-empty__icon">⚠️</div>
          <h3>{error}</h3>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="proc-results-info">
            <span>
              Найдено: <strong>{filtered.length}</strong>
            </span>
            <span>Всего назначено: {myRequests.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="proc-empty proc-empty--large">
              <div className="proc-empty__icon">🔎</div>
              <h3>Заявок нет</h3>
              <p>
                {myRequests.length === 0
                  ? "Вам ещё не назначили ни одной заявки."
                  : "Попробуйте изменить фильтр или поиск."}
              </p>
            </div>
          ) : (
            <div className="proc-table-wrap">
              <table className="proc-table">
                <thead>
                  <tr>
                    <th>Заявка</th>
                    <th>Товар</th>
                    <th>Клиент</th>
                    <th>Администратор</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() =>
                        navigate(`/procurement/requests/${r.id}`)
                      }
                    >
                      <td>
                        <strong>{r.number}</strong>
                        <div className="proc-muted">ID: {r.id}</div>
                      </td>
                      <td>
                        {r.title || (
                          <span className="proc-muted">
                            {(r.description || "").slice(0, 60) || "—"}
                          </span>
                        )}
                      </td>
                      <td>
                        {r.client_name || (
                          <span className="proc-muted">—</span>
                        )}
                      </td>
                      <td>
                        {r.client_manager_name || (
                          <span className="proc-muted">
                            #{r.client_manager_id}
                          </span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}